import { CurrencyCode } from '@dayetopia/types';
import { Offer, ProductPriceData, SEOPriceData } from '@contracts';
import { removeLocalesFromPath, getLocaleUrlForPath } from '@utils/language';
import i18n from '../../i18n';
import { countriesInEULocale } from './country';
import useCurrentURLLanguage from './useCurrentUrlLanguage';

export const generateProductSchema = (productData: any, productPriceData: any[], seoTitle: string, description: string, uid?: string) => {
  const { productOptions } = productData;

  const localeToCurrencyMap = {
    'en-gb': 'GBP',
    'en-eu': 'EUR',
    'en-ie': 'EUR',
    'en-de': 'EUR',
    'en-bg': 'BGN',
    'en-us': 'USD'
  };

  const createOffer = (sku: string, priceData: SEOPriceData, price: ProductPriceData): Offer => ({
    '@type': 'Offer' as const,
    sku,
    name: priceData.name,
    price: price.amount.toFixed(2),
    priceCurrency: price.currency,
    itemCondition: 'http://schema.org/NewCondition',
    availability: 'http://schema.org/InStock'
  });

  const offers: Offer[] = [];

  const addOffers = (sku: string, priceData: SEOPriceData) => {
    if (!priceData) {
      return;
    }

    const currentLocale = useCurrentURLLanguage();

    if (!currentLocale) {
      return; // Return null if locale data is missing
    }

    const isValidLocale = (locale: string): locale is keyof typeof localeToCurrencyMap => {
      return locale in localeToCurrencyMap;
    };

    const targetCurrency = isValidLocale(currentLocale.locale) ? localeToCurrencyMap[currentLocale.locale] : undefined;

    const prices = priceData.pricePerPlan?.[0]?.price || priceData.price;
    const priceList = Array.isArray(prices)
      ? prices
          .filter((price) => price.currency === targetCurrency)
          .map((price) => {
            const finalPrice = { ...price };
            if (price.currency === CurrencyCode.GBP) {
              const vatPercent = priceData.vat?.GB ?? 0;
              finalPrice.amount = price.amount * (1 + vatPercent / 100);
            }
            return createOffer(sku, priceData, finalPrice);
          })
      : [];

    if (priceList) offers.push(...priceList);
  };

  productOptions.forEach((option: { one_off_sku?: { text: string }; subscription_sku?: { text: string } }) => {
    const { one_off_sku, subscription_sku } = option;

    if (one_off_sku?.text) {
      const priceData = productPriceData?.find((data) => data.id__normalized === one_off_sku.text);
      addOffers(one_off_sku.text, priceData);
    }

    if (subscription_sku?.text) {
      const priceData = productPriceData?.find((data) => data.id__normalized === subscription_sku.text);
      addOffers(subscription_sku.text, priceData);
    }
  });

  if (offers.length === 0) {
    const priceData = productPriceData.find((data) => data.id__normalized === productData.productCode);
    addOffers(productData.productCode, priceData);
  }

  if (!seoTitle || !description || offers.length === 0) {
    return null;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: seoTitle,
    image: productData.images[0]?.product_image.url || '',
    mpn: uid,
    brand: {
      '@type': 'Brand',
      name: 'Daye'
    },
    description,
    offers
  };

  return JSON.stringify(schema, null, 2);
};

export const generateHreflangTags = (baseUrl: string, pathname: string) => {
  const strippedPathname = removeLocalesFromPath(pathname);

  const hreflangTags = [{ hreflangTag: 'x-default', hrefTag: `${baseUrl}${strippedPathname}` }];

  i18n.forEach((item) => {
    const { locale, path } = item;
    if (path !== 'en-eu' || locale !== 'en-eu') {
      const localeUrl = getLocaleUrlForPath(strippedPathname, path, true);
      hreflangTags.push({
        hreflangTag: locale || path,
        hrefTag: `${baseUrl}${localeUrl}`
      });
    }
  });

  countriesInEULocale.forEach((country) => {
    hreflangTags.push({
      hreflangTag: `en-${country}`,
      hrefTag: `${baseUrl}/en-eu${strippedPathname}`
    });
  });

  return hreflangTags;
};
